import React, { useRef, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ref, uploadBytes } from 'firebase/storage';
import { Loader2 } from 'lucide-react';
import { auth, ensureAuth, storage } from '../../utils/firebase';
import { parseQuoteTotalInput } from '../../utils/rfqQuoteTotal';

const MAX_QUOTE_BYTES = 25 * 1024 * 1024;

type TradeBriefingQuoteUploadProps = {
  token: string;
  pin?: string;
  companyName: string;
  defaultLabel: string;
};

function validateQuotePdf(file: File): string | null {
  const name = file.name.toLowerCase();
  const isPdf = file.type === 'application/pdf' || name.endsWith('.pdf');
  if (!isPdf) return 'Quote file must be a PDF.';
  if (file.size > MAX_QUOTE_BYTES) return 'PDF must be under 25 MB.';
  return null;
}

function sanitizeQuoteFileName(name: string): string {
  const base = (name || 'quote.pdf').replace(/[^\w.\-]+/g, '_').slice(0, 120);
  const withoutExt = base.replace(/\.pdf$/i, '');
  return `${withoutExt || 'quote'}.pdf`;
}

const TradeBriefingQuoteUpload: React.FC<TradeBriefingQuoteUploadProps> = ({
  token,
  pin,
  companyName,
  defaultLabel,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [quoteTotal, setQuoteTotal] = useState('');
  const [notes, setNotes] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedLabel, setSubmittedLabel] = useState<string | null>(null);

  const handleFile = (next: File | null) => {
    if (!next) {
      setFile(null);
      return;
    }
    const problem = validateQuotePdf(next);
    if (problem) {
      setError(problem);
      setFile(null);
      return;
    }
    setError(null);
    setFile(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Attach your quote PDF.');
      return;
    }
    const total = parseQuoteTotalInput(quoteTotal);
    if (!total) {
      setError('Enter a valid quote amount.');
      return;
    }
    const fileError = validateQuotePdf(file);
    if (fileError) {
      setError(fileError);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await ensureAuth();
      const uid = auth.currentUser?.uid;
      if (!uid) throw new Error('Sign in required to upload a quote.');
      const safeName = sanitizeQuoteFileName(file.name);
      const storagePath = `trade-briefing-quote-uploads/${uid}/${Date.now()}_${safeName}`;
      await uploadBytes(ref(storage, storagePath), file, {
        contentType: file.type || 'application/pdf',
      });

      const fns = getFunctions(undefined, 'us-central1');
      const submit = httpsCallable<
        {
          token: string;
          pin?: string;
          storagePath: string;
          fileName: string;
          quoteTotal: number;
          quoteLabel?: string;
          notes?: string;
        },
        { submissionId: string; quoteLabel: string }
      >(fns, 'submitTradeBriefingQuote');
      const result = await submit({
        token,
        pin: pin || undefined,
        storagePath,
        fileName: file.name,
        quoteTotal: total,
        quoteLabel: defaultLabel,
        notes: notes.trim() || undefined,
      });
      setSubmittedLabel(result.data.quoteLabel || defaultLabel);
      setFile(null);
      setQuoteTotal('');
      setNotes('');
      setShowNote(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: string }).message)
          : 'Could not submit quote.';
      setError(message.replace(/^Firebase:\s*/i, '').replace(/\s*\([^)]*\)\s*$/, '') || 'Could not submit quote.');
    } finally {
      setBusy(false);
    }
  };

  if (submittedLabel) {
    return (
      <section className="tb-quote" aria-live="polite">
        <p className="tb-quote-success">
          Quote received{companyName ? ` for ${companyName}` : ''}: {submittedLabel}.{' '}
          <button type="button" className="tb-text-btn" onClick={() => setSubmittedLabel(null)}>
            Send another
          </button>
        </p>
      </section>
    );
  }

  return (
    <section className="tb-quote" aria-labelledby="tb-quote-heading">
      <div className="tb-quote-copy">
        <p className="tb-kicker" id="tb-quote-heading">Quote</p>
        <p>PDF and total. Files under Other Quotes as {companyName || 'your company'}.</p>
      </div>
      <form className="tb-quote-form" onSubmit={e => void handleSubmit(e)}>
        <label className={`tb-quote-file${file ? ' has-file' : ''}`}>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={e => handleFile(e.target.files?.[0] ?? null)}
          />
          <span>{file ? file.name : 'Choose PDF'}</span>
        </label>
        <label className="tb-quote-amount">
          <span className="tb-sr-only">Quote total (USD)</span>
          <input
            inputMode="decimal"
            value={quoteTotal}
            onChange={e => setQuoteTotal(e.target.value)}
            placeholder="Amount"
            autoComplete="off"
            aria-label="Quote total in US dollars"
          />
        </label>
        <button type="submit" className="tb-btn" disabled={busy}>
          {busy ? <Loader2 className="tb-spin" size={14} aria-hidden /> : null}
          {busy ? 'Sending' : 'Send'}
        </button>
        <button
          type="button"
          className="tb-text-btn"
          onClick={() => setShowNote(open => !open)}
          aria-expanded={showNote}
        >
          {showNote ? 'Hide note' : '+ Note'}
        </button>
        {showNote ? (
          <textarea
            className="tb-quote-note"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="Lead time, exclusions…"
            maxLength={2000}
          />
        ) : null}
        {error ? <p className="tb-quote-error">{error}</p> : null}
      </form>
    </section>
  );
};

export default TradeBriefingQuoteUpload;
